// Calendario mensual (§ 3.5): tareas, llamadas y reuniones del mes.
import type { JSX } from "react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api.ts";
import { Button, Card, Loading } from "../components/ui.tsx";

interface CalItem { id: string; kind: "task" | "call" | "meeting"; title: string; date: string; allDay: boolean; }
const KIND_COLOR: Record<string, string> = { task: "bg-amber-100 text-amber-800", call: "bg-blue-100 text-blue-800", meeting: "bg-purple-100 text-purple-800" };
const DOW = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

function ymd(d: Date): string { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; }

export function Calendar(): JSX.Element {
  const navigate = useNavigate();
  const [cursor, setCursor] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);

  const cal = useQuery<{ items: CalItem[] }>({
    queryKey: ["calendar", ymd(first), ymd(last)],
    queryFn: () => api.get(`/calendar?from=${ymd(first)}&to=${ymd(last)}`),
  });

  const byDay = useMemo(() => {
    const m = new Map<string, CalItem[]>();
    for (const it of cal.data?.items ?? []) {
      const key = it.date.slice(0, 10);
      const arr = m.get(key) ?? [];
      arr.push(it);
      m.set(key, arr);
    }
    return m;
  }, [cal.data]);

  // Grilla comenzando en lunes.
  const startOffset = (first.getDay() + 6) % 7;
  const cells: Array<Date | null> = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= last.getDate(); d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);

  const monthName = cursor.toLocaleDateString("es-CO", { month: "long", year: "numeric" });
  const today = ymd(new Date());

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button className="btn-ghost btn-sm" onClick={() => navigate("/activities")}>←</button>
          <h1 className="text-xl font-bold text-ink capitalize">{monthName}</h1>
        </div>
        <div className="flex gap-2">
          <Button small onClick={() => setCursor(new Date(year, month - 1, 1))}>‹</Button>
          <Button small onClick={() => { const n = new Date(); setCursor(new Date(n.getFullYear(), n.getMonth(), 1)); }}>Hoy</Button>
          <Button small onClick={() => setCursor(new Date(year, month + 1, 1))}>›</Button>
        </div>
      </div>
      {cal.isLoading ? <Loading /> : (
        <Card className="p-3">
          <div className="grid grid-cols-7 gap-px">
            {DOW.map((d) => <div key={d} className="text-xs font-semibold text-ink-muted text-center py-1">{d}</div>)}
            {cells.map((c, i) => {
              const key = c ? ymd(c) : "";
              const items = c ? byDay.get(key) ?? [] : [];
              return (
                <div key={i} className={`min-h-24 border border-line rounded p-1 ${c ? "" : "bg-canvas"}`}>
                  {c && (
                    <>
                      <div className={`text-xs mb-1 ${key === today ? "font-bold text-primary" : "text-ink-muted"}`}>{c.getDate()}</div>
                      <div className="space-y-0.5">
                        {items.slice(0, 4).map((it) => (
                          <div key={it.id} className={`text-[11px] px-1 py-0.5 rounded truncate ${KIND_COLOR[it.kind]}`} title={it.title}>{it.title}</div>
                        ))}
                        {items.length > 4 && <div className="text-[10px] text-ink-muted">+{items.length - 4} más</div>}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}
